import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useStation } from '../../contexts/StationContext';
import { useLocation } from 'react-router';
import { getBanners } from '../../services/commonService';
import { getFile, getFileId, getFileMetadata } from '../../services/fileService';
import { Modal } from '../../components/ui/Modal';
import { YoutubeEmbeded } from './YoutubeEmbeded';
import ChevronLeft from '../../assets/icons/chevronLeft.svg?react';
import ChevronRight from '../../assets/icons/chevronRight.svg?react';
export interface CarouselProps {
  content?: CarouselItem[];
  slideDelay?: number;
  animationSpeed?: number;
  className?: string;
}
export type CarouselItem = {
  type: 'Image' | 'Video';
  imageUrl?: string;
  mobileImageUrl: string;
  imageAlt?: string;
  mobileImageAlt?: string;
  videoUrl?: string;
  redirectUrl?: string;
};

export const Carousel: React.FC<CarouselProps> = ({
  content: initialContent = [],
  slideDelay = 3000,
  animationSpeed = 800,
  className = ''
}) => {
  const [content, setContent] = useState<Array<any>>(initialContent);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showYoutubeEmbed, setShowYoutubeEmbed] = useState(false);
  const [youtubeEmbedUrl, setYoutubeEmbedUrl] = useState();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { station } = useStation();
  const { pathname } = useLocation();

  useEffect(() => {
    if (station?.id) {
      getBanners(getPage(), station?.id).then(async response => {
        // Process each banner to fetch alt text
        const bannersWithAlt = await Promise.all(response.data.map(async banner => {
          let imageAlt = banner.title; // Default to title
          let mobileImageAlt = banner.title; // Default to title

          // Fetch alt text for desktop image
          if (banner?.mediaType === 'Image' && banner?.imageUrl) {
            try {
              const fileId = getFileId(banner.imageUrl);
              const metadata = await getFileMetadata(fileId);
              imageAlt = metadata.altName || banner.title;
            } catch (error) {
              console.error('Error fetching desktop banner alt text:', error);
            }
          }

          // Fetch alt text for mobile image
          if (banner?.mediaType === 'Image' && banner?.mobileImageUrl) {
            try {
              const fileId = getFileId(banner.mobileImageUrl);
              const metadata = await getFileMetadata(fileId);
              mobileImageAlt = metadata.altName || banner.title;
            } catch (error) {
              console.error('Error fetching mobile banner alt text:', error);
            }
          }

          return {
            type: banner.mediaType as 'Image' | 'Video',
            imageUrl: banner?.mediaType === 'Image' && banner?.imageUrl && getFile(banner?.imageUrl),
            mobileImageUrl: banner?.mediaType === 'Image' && banner?.mobileImageUrl && getFile(banner?.mobileImageUrl),
            imageAlt,
            mobileImageAlt,
            videoUrl: banner?.videoUrl,
            redirectUrl: banner.redirectUrl
          };
        }));
        
        setContent(bannersWithAlt);
      }).catch(error => {
        console.error('Error fetching banners:', error);
      });
    }
  }, [station, pathname]);

  const getPage = () => {
    switch (pathname.split('/')[2]) {
      case 'home':
        return 'Home';
      case 'audio':
        return 'Audio';
      case 'video':
        return 'Video';
      case 'events':
        return 'Event';
      case 'redlines':
        return 'Red Lines';
      case 'talent-hunt':
        return 'Talent Hunt';
      default:
        return 'Home';
    }
  }

  const contentLength = React.useMemo(() => content.length, [content]);

  // Restart slideshow when it reaches the end
  useEffect(() => {
    if (currentIndex === contentLength) {
      setCurrentIndex(0);
    }
  }, [currentIndex, contentLength]); // Restart logic here

  // Automatic sliding
  const startAutoSlide = useCallback(() => {
    if (!isHovered && !showYoutubeEmbed) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      intervalRef.current = setInterval(() => {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % (contentLength || 1)); // Adjust to prevent errors
      }, slideDelay);
    }
  }, [contentLength, slideDelay, isHovered, showYoutubeEmbed]);

  const stopAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      console.log("Stopping auto slide")
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isHovered || showYoutubeEmbed) {
      stopAutoSlide()
    } else {
      startAutoSlide()
    }
  }, [isHovered, showYoutubeEmbed, startAutoSlide, stopAutoSlide])

  // useEffect(() => {
  //   startAutoSlide();
  //   return () => stopAutoSlide();
  // }, [startAutoSlide, stopAutoSlide]);

  // Restart slideshow when returning to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCurrentIndex(0); // Reset to the first slide
        startAutoSlide();
      } else {
        stopAutoSlide();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [startAutoSlide, stopAutoSlide]);


  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 1
    }),
    center: {
      x: 0,
      opacity: 1,
      zIndex: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 1,
      zIndex: 0
    })
  };
  const handleClick = () => {
    if (content[currentIndex]?.type === 'Image') {
      if (content[currentIndex]?.videoUrl) {
        setYoutubeEmbedUrl(content[currentIndex]?.videoUrl)
        setShowYoutubeEmbed(true)
      } else {
        window.open(content[currentIndex]?.redirectUrl, '_blank')
      }
    }
  }
  const onCloseYoutubeEmbeded = () => {
    setShowYoutubeEmbed(false)
  }
  const onLeftClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const newIndex = currentIndex - 1
    if (newIndex > 0) {
      setCurrentIndex(newIndex);
    } else {
      setCurrentIndex(contentLength - 1);
    }
    setDirection(newIndex > currentIndex ? 1 : -1);
  }
  const onRightClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const newIndex = currentIndex + 1
    if (newIndex < contentLength) {
      setCurrentIndex(newIndex);
    } else {
      setCurrentIndex(0);
    }
    setDirection(newIndex > currentIndex ? 1 : -1);
  }
  return (

    <div className={`relative bg-black aspect-[22/9] w-full h-full overflow-hidden ${className}`}>
      <div className='flex flex-col items-center justify-center gap-4 w-full'>
        <Modal className='w-full h-full pt-[50px] z-[501]' isOpen={showYoutubeEmbed} onClose={() => onCloseYoutubeEmbeded()}>
          <YoutubeEmbeded url={youtubeEmbedUrl} />
        </Modal>
      </div>
      <div
        className="relative w-full h-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >

        <div className="absolute m-auto top-0 bottom-0 z-[40] left-2" onClick={(e) => e.stopPropagation()}>
          <div className='flex justify-center items-center h-full ' onClick={(e) => onLeftClick(e)}>
            <button className="flex justify-center items-center shrink-0 p-1 text-white bg-white bg-opacity-25 hover:bg-opacity-50 border border-[#D0D5DD] rounded-full h-8 w-8 focus:outline focus:outline-2 focus:outline-white focus:-outline-offset-2">
              <ChevronLeft />
            </button>
          </div>
        </div>
        <div className="absolute m-auto top-0 bottom-0 right-2 z-[40]" onClick={(e) => e.stopPropagation()}>
          <div className='flex justify-center items-center h-full ' onClick={(e) => onRightClick(e)}>
            <button className="flex justify-center items-center shrink-0 p-1 text-white bg-white bg-opacity-25 hover:bg-opacity-50 border border-[#D0D5DD] rounded-full h-8 w-8 focus:outline focus:outline-2 focus:outline-white focus:-outline-offset-2">
              <ChevronRight />
            </button>
          </div>
        </div>


        <AnimatePresence initial={false} custom={direction} mode="sync">
          <motion.div
            onClick={() => handleClick()}
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "tween", duration: animationSpeed / 1000 },
              opacity: { duration: animationSpeed / 1000 }
            }}
            className="absolute w-full h-full z-[30]"
          >
            {content[currentIndex]?.type === 'Image' && (
              <>
                <img
                  src={content[currentIndex].mobileImageUrl || content[currentIndex].imageUrl}
                  alt={content[currentIndex].mobileImageAlt || content[currentIndex].imageAlt || `Slide ${currentIndex + 1}`}
                  className="lg:hidden md:hidden h-full aspect-[22/9] m-auto"
                  draggable={false}
                />
                <img
                  src={content[currentIndex].imageUrl}
                  alt={content[currentIndex].imageAlt || `Slide ${currentIndex + 1}`}
                  className="h-full aspect-[22/9] m-auto"
                  draggable={false}
                />
              </>
            )}
            {content[currentIndex]?.type === 'Video' && (
                
              <YoutubeEmbeded url={content[currentIndex]?.videoUrl} />
            )}
          </motion.div>
        </AnimatePresence>
      
        {/* Square indicators */}
        <div className="absolute bg-[#D1E1E633] p-2 md:p-3 bottom-2 md:bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-[1] rounded-sm" onClick={(event) => event.stopPropagation()}>
          {content.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 transition-colors ${index === currentIndex ? 'bg-white' : 'bg-white/50'
                } hover:bg-white`}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
            />
          ))}
        </div>
      </div>

    </div>
  );
};